import {useSessionStore, useAppStore, useAnnouncementStore, useUserStore} from '@/stores'
import useNetworkMachine from '@/state/useNetworkMachine'
import {useLocalStream} from '@/composables'
import {jsonRpc, mediator, messenger} from '@communication'
import {procedures} from '@communication'
import {useSnapshot} from '@/composables/useSnapshot'


const env = import.meta.env

export default class ParticipantDisconnectedProcedure {
  constructor() {
    this.name = 'participantDisconnectedProcedure'
    this.roleLeave = ''
    this.graceTimeout = null
  }

  install = () => {
    jsonRpc.on('disconnected', this.handler)
    messenger.subscribe(this.name, 'websocket:reconnecting', this.handleReconnecting)
    messenger.subscribe(this.name, 'websocket:reconnected', this.handleReconnected)
    messenger.subscribe(this.name, 'websocket:rejoined', this.handleRejoined)
  }

  uninstall = () => {
    jsonRpc.off('disconnected', this.handler)
    messenger.unsubscribe(this.name)
  }

  handler = (event) => {
    const {id: participantId} = event.detail
    const session = useSessionStore()
    const participant = session.getParticipant(participantId)
    // Unknown or already removed (a second 'disconnected' for the same party): nothing to announce
    if (!participant) return
    session.addParticipantsLeave(participant.party + '_' + participantId)
    this.roleLeave = participant.party
    if (participant.party === 'client' || participant.party === 'assistant') {
      const announcement = useAnnouncementStore()
      const app = useAppStore()
      if(app.isLoading){
        app.loaded();
      }
      if (app.state.matches('app.recording.on') && participant.party === 'client') {
        procedures.get('recordingProcedure').stop()
      }
      if (app.state.matches('app.view.meeting.draw')) {
        messenger.emit('board:signal', 'snapshot:save')
        const {dismiss} = useSnapshot();
        dismiss('snapshot')
      }
      announcement.hideMessage()

      app.transition('lobby')
      session.removeParticipant(participantId)
    } else {
      session.removeParticipant(participantId)
    }
    const {send, state} = useNetworkMachine()
    send('LOST_CONNECTION')
  }

  handleReconnecting = () => {
    const {send} = useNetworkMachine()
    send('LOST_CONNECTION')

    const session = useSessionStore()
    const userStore = useUserStore()
    session.addParticipantsLeave(userStore.party + '_' + userStore.id)
    this.graceTimeout = setTimeout(async () => this.moveToExit(), env.VITE_GRACE_TIMEOUT)
  }

  handleReconnected = () => {
    clearTimeout(this.graceTimeout)
    // In a call an open socket is not enough: "reconnecting" stays until the re-join has succeeded, which JsonRpc retries
    // for up to about 30 s while the server still holds the old socket. Elsewhere there is nothing to re-join
    if (!useAppStore().state.matches('app.view.meeting')) {
      this.handleRejoined()
    }
  }

  handleRejoined = () => {
    const session = useSessionStore()
    const userStore = useUserStore()
    const participantLeave = userStore.party + '_' + userStore.id
    session.removeParticipantsLeave(participantLeave)

    const {send} = useNetworkMachine()
    send('ONLINE_CONNECTION')
  }

  async moveToExit() {
    console.error(`Grace timeout ${jsonRpc.status}`)
    const {stream} = useLocalStream()
    // Only a call is left; before joining (an outage on the join screen) the camera and mic stay on
    if (jsonRpc.status === 'DISCONNECTED' && useAppStore().state.matches('app.view.meeting')) {
      const app = useAppStore()
      stream.video.stop()
      stream.audio.stop()
      app.transition('exit')
    }
  }
}
